/**
 * ssrComposite — SSR OWNERSHIP (render wave 1, 2026-09-17).
 *
 * three's r185 SSR example ADDS the reflection onto the scene colour, and the
 * pipeline copied it. The scene colour already carries the environment's
 * indirect specular (the sky PMREM), so a metal or a puddle reflected twice
 * wherever a screen-space ray hit — `intensity 0.6` was the damping. This is
 * the CPU mirror of the pipeline's TSL: a hit replaces `SSR_REPLACE` of the
 * scene colour by metalness — a metal's beauty is almost all indirect
 * specular, a dielectric keeps its diffuse and gains only the Fresnel-weighted
 * reflection the node already scales.
 *
 * `SSR_REPLACE` < 1 keeps a little of the IBL for tint and occlusion: the
 * non-stochastic node does not tint its reflection by the albedo.
 *
 * CONFIDENCE, NOT A BINARY HIT (shader gaps program, 2026-09-29). The vendored
 * node's blur path now writes the hit's CONFIDENCE as alpha — the weight its
 * colour already took (distance attenuation × grazing factor), 0 on a miss —
 * and the roughness blur averages it with its neighbours, so a blurred edge
 * pixel carries its real coverage. The composite replaces only that fraction:
 * `scene · (1 − c·m·k) + ssr.rgb`. The old `step(1e-4, a)` treated any sliver
 * of blurred hit as a full hit and removed the metal's whole specular behind a
 * faint reflection — the dark rim around every reflection, and a dark mirror
 * seen straight down, where the grazing factor (and so the reflection) is 0.
 * Where the screen-space result fades (far hits, grazing, the edge of a
 * reflection) the IBL already in the scene colour is the fallback, by weight —
 * never an abrupt per-pixel switch.
 */
export const SSR_REPLACE = 0.85;

/** Below this the node wrote no hit (kept for callers of `ssrHit`). */
export const SSR_HIT_EPS = 1e-4;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** A hit at all (any confidence above the epsilon). */
export function ssrHit(a: number): number { return a > SSR_HIT_EPS ? 1 : 0; }

/** The hit's confidence — the node's (blurred) alpha, clamped to [0, 1]. */
export function ssrConfidence(a: number): number { return clamp01(a); }

/** The fraction of the scene colour the reflection replaces. */
export function ssrCompositeWeight(a: number, metalness: number): number {
  return ssrConfidence(a) * clamp01(metalness) * SSR_REPLACE;
}

export function ssrCompositeCpu(
  scene: [number, number, number],
  ssr: [number, number, number, number],
  metalness: number,
): [number, number, number] {
  const w = ssrCompositeWeight(ssr[3], metalness);
  return [scene[0] * (1 - w) + ssr[0], scene[1] * (1 - w) + ssr[1], scene[2] * (1 - w) + ssr[2]];
}
